import { PAGE_PERMISSION } from "@/permissions";
import { needsOneStore } from "@/storeScope";
import { locale, tr } from "@/i18n";
/**
 * The panel's navigation, defined once.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The sidebar and the home page both have to show the same set of pages to the
 * same person. When the list lived inside the sidebar component, the only way
 * to build a second view of it was to copy it, and a copy drifts. Both now read
 * this file, so they cannot disagree.
 *
 * THE THREE WAYS A PAGE DISAPPEARS
 * --------------------------------
 * 1. status: false      the page exists in the repository but is not finished.
 *                       Flip it to true when it is. Nothing else to change.
 * 2. allowedRoles       who may see it. Absent means administrators only, which
 *                       is deliberately the safe default. Roles come from
 *                       mc_users.utype: 1 admin, 2 store manager, anything else
 *                       staff.
 * 3. VUE_APP_HIDDEN_PAGES  a per-brand list of route names, set at build time,
 *                       for pages a given brand's backend does not serve.
 *
 * Any one of the three hides an item. None of them is a security control: the
 * API decides what a person may actually do. This decides what is easy to find.
 *
 * A fourth applies while the top bar shows "All stores": the pages that work one
 * store at a time (src/storeScope.js) leave the menu until a store is picked.
 *
 * Every `name` here must exist in src/router/routes.js, or the item is dead.
 */

export const mainMenu = [

  // ── Sales Reports ─────────────────────────────────
  // Everything in this group is served by the external reporting
  // service (reports.androvega.ca). We cannot change those numbers, so
  // they are kept together and away from the pages we do control. Put a
  // page here ONLY if it uses reportMixin.
  {
    type: "salesGroup",
    title: "Sales Reports",
    status: true,
    items: [
      { type: "subheader", title: "SALES" },
      { title: "Overview", name: "analytics", note: "Takings and orders for the last few days at a glance" },
      { title: "Daily", name: "analytics-daily", note: "One day, hour by hour" },
      { title: "Shift", name: "analytics-shift", note: "Split by shift rather than by clock hour" },
      { title: "Weekly", name: "analytics-weekly", note: "One week, day by day" },
      { title: "Monthly", name: "analytics-monthly", note: "One month, and how it compares with the last" },
      { title: "Pick your own dates", name: "analytics-custom", note: "Any two dates you choose" },
      { type: "subheader", title: "WHAT SOLD, AND WHEN" },
      { title: "What sold", name: "analytics-productmix", note: "Which items sold, and how much each brought in" },
      { title: "Sales by size", name: "breakdown-size-sales", note: "How the pizza sizes sell against each other" },
      { title: "Orders by hour", name: "analytics-hourly-heatmap", note: "Which hours of which days are busy" },
      { title: "Lunch, dinner, late", name: "breakdown-day-part", note: "The day split into trading periods" },
      { title: "Where orders came from", name: "breakdown-channel-mix", note: "Website, app, phone and the delivery platforms" },
      { title: "Online Orders", name: "analytics-online-orders", note: "Orders placed on the website and in the apps" },
      // Renamed: there is a different "Delivery Map" under Settings that
      // edits delivery zones. Two menu items with one name is why people
      // could not tell them apart.
      { title: "Where deliveries went", name: "breakdown-delivery-heatmap", note: "A map of the addresses orders were sent to" },
      { type: "subheader", title: "STORE COMPARISON" },
      { title: "Store scorecard", name: "store-scorecard", note: "Each store's own numbers, one card per store" },
      { title: "Store league table", name: "store-benchmark", note: "Every store ranked against the others" },
      { type: "subheader", title: "MONEY" },
      { title: "Average order value", name: "financials-avg-ticket", note: "What a typical order is worth, over time" },
      { title: "Discounts & Voids", name: "financials-discounts-voids", note: "Money taken off at the till, and orders cancelled" },
      { title: "How people paid", name: "financials-payment-breakdown", note: "Card, cash and the rest" },
      // Hidden: still a placeholder. The file stays in the repository.
      { title: "Menu Engineering", name: "breakdown-menu-engineering", status: false },
      { title: "Food Cost AvT", name: "financials-food-cost-report", status: false },
    ],
  },

  // ── Performance ──────────────────────────────────
  // Fed by our own API, so these numbers are ours to fix. Deliberately
  // separate from Sales Reports above: the same week can read differently
  // on the two sides because the definitions differ.
  { type: "header", title: "PERFORMANCE" },
  { title: "Sales summary", name: "analytics-hq-weekly", svgIcon: "calendar-week", status: true, note: "Net sales per store for any dates, against the days before and last year. A Monday to Sunday week is the figure royalties are worked out from" },
  // Renamed from "Store Dashboard" so it is not mistaken for the store
  // comparison reports in the group above.
  { title: "Store performance", name: "store-dashboard", svgIcon: "ic-dashboard", status: true, note: "Every trading store side by side, today against yesterday" },
  { title: "Busiest hours", name: "store-peak-times", svgIcon: "trending-up", status: true, note: "When each store takes most of its orders" },
  // Keep the name "deal-analytics": the analytics overlay
  // (public/ga4/panel.js) activates on exactly that string. Renaming it blanks
  // the page.
  { title: "Website & app traffic", name: "deal-analytics", svgIcon: "monitor", status: true, note: "Visitors, orders and where they came from, plus which deals and coupons were used" },
  { title: "Visits that became orders", name: "analytics-conversion", svgIcon: "percent", status: true, note: "How many people who looked ended up ordering" },
  { title: "Customer Insights", name: "store-customer-insights", status: false },

  // ── Finance ──────────────────────────────────────
  { type: "header", title: "FINANCE" },
  { title: "Royalty & fees", name: "financials-royalty", svgIcon: "dollar-sign", status: true, note: "What each store owes in royalty and marketing fees, month by month" },

  // ── Marketing & CRM ──────────────────────────────
  { type: "header", title: "MARKETING & CRM" },
  { title: "Banners", name: "banners", icon: "&#xe82d;", status: true, allowedRoles: ["admin", "manager"], note: "The promotional images customers see in the app" },
  { title: "Push Notifications", name: "push-notifications", icon: "&#xe80f;", status: true, allowedRoles: ["admin"], note: "Send a message to the app on a customer's phone" },
  { title: "SMS Campaigns", name: "sms", icon: "&#xe80c;", status: true, allowedRoles: ["admin"], note: "Send a text message to customers" },
  { title: "Coupons", name: "coupons", icon: "&#xe801;", status: true, allowedRoles: ["admin", "manager"], note: "Create a code customers can type in at checkout" },
  {
    type: "dealEngineGroup",
    title: "Deal Engine",
    status: true,
    items: [
      { title: "All deals", name: "deals-dashboard", note: "Every offer, and whether it is switched on" },
      { title: "Deal Builder", name: "deal-builder", note: "Create or change an offer, step by step" },
      { title: "Suggested deals", name: "intelligence-dashboard", note: "Offers the system has drafted from what has been selling" },
      { title: "Who sees a deal", name: "rule-builder", note: "Decide which customers an offer is shown to" },
            { title: "Offer performance", name: "deal-offer-performance", note: "How often each offer was shown, and what came of it" },
      // Hidden: placeholders. Files stay in the repository.
      { title: "Cross-Sell", name: "cross-sell-manager", status: false },
      { title: "A/B Tests", name: "ab-test-manager", status: false },
    ],
  },
  // Was a two-item group whose second item was a placeholder. A
  // collapsible holding one page is just a page.
  { title: "Customers", name: "customers", svgIcon: "ic-users", status: true, note: "Every registered account, and what they have spent" },
  { title: "Customer creations", name: "creations", svgIcon: "ic-social", status: true, allowedRoles: ["admin", "manager"], note: "Pizzas customers built and shared in the app: approve, flag or hide them, and review their photos" },
  // The admin.php loyalty tab, moved into Connect (MB 2026-10-15; PG and LR 2026-10-23, owner: "admin paneli
  // kullanılmıyor artık"). Brand-wide customer data: administrators, unless a role gives the loyalty.* permissions.
  { title: "Loyalty", name: "loyalty-dashboard", svgIcon: "ic-loyalty", status: true, allowedRoles: ["admin", "manager"], note: "Customers' tiers, points and weekly streaks; give or take back points" },
  {
    type: "crmAnalyticsGroup",
    title: "CRM Analytics",
    status: true,
    items: [
      { title: "New customers", name: "crm-acquisition", note: "How many people signed up, and how many went on to order" },
      { title: "Do they come back?", name: "crm-cohorts", note: "Of the people who joined in a given month, how many ordered again" },
      { title: "Customers who stopped", name: "crm-lapsed", note: "People who used to order and have gone quiet" },
      { title: "What the offers cost", name: "crm-campaign-performance", note: "What coupons and deals gave away, and what came back" },
    ],
  },
  { title: "Campaign links", name: "marketing-utm-links", svgIcon: "ic-social", status: true, allowedRoles: ["admin", "manager"], note: "Build a trackable link for a post or an email" },
  // Shown by the role's blog.view (permissions.js); a brand whose API has no /blog has no such permission.
  { title: "Website blog", name: "website-blog", svgIcon: "ic-blog", status: true, allowedRoles: ["admin"], note: "The stories on the website's Our Stories page: write, change or remove them" },
  { type: "link", title: "Social Hub", name: "socialHub", svgIcon: "ic-social", href: "/social/", status: true, note: "The separate tool for scheduling social posts" },
  // Hidden: every one of these is still a "Coming soon" placeholder.
  // The files stay in the repository; flip status to true when built.
  { title: "Marketing", name: "marketing", svgIcon: "ic-mail", status: false },
  { title: "Email Campaigns", name: "email", svgIcon: "ic-mail", status: false },
  { title: "Flash Deals", name: "flash-deals", svgIcon: "ic-flash", status: false },
  { title: "Gift Cards", name: "gift-cards", svgIcon: "ic-gift", status: false },
  { title: "New Signups", name: "signups", status: false },
  { title: "Pipeline", name: "pipeline", svgIcon: "ic-pipeline", status: false },
  { title: "Segments", name: "segments", svgIcon: "ic-segment", status: false },
  { title: "Cases", name: "cases", svgIcon: "ic-cases", status: false },
  { title: "Automation", name: "automation", svgIcon: "ic-auto", status: false },
  { title: "Social Templates", name: "socialTemplates", svgIcon: "ic-social", status: false },
  { title: "Location Analytics", name: "locationAnalytics", svgIcon: "ic-location", status: false },

  // ── Atlas ────────────────────────────────────────
  // atlasintelligence.tech, opened in a new tab already signed in as the same
  // person (src/utils/atlasSso.js). Not pages of this panel, so not routes:
  // each needs atlas.sms / atlas.ai, which administrators have and other roles
  // get on the roles page.
  { type: "header", title: "ATLAS" },
  { type: "sso", title: "SMS Panel", name: "atlas-sms", sso: "sms", svgIcon: "ic-mail", status: true, note: "Atlas's SMS panel, signed in as you" },
  { type: "sso", title: "AI Panel", name: "atlas-ai", sso: "ai", svgIcon: "ic-flash", status: true, note: "Atlas's AI Studio and archive, signed in as you" },

  // ── Menu & Products ──────────────────────────────
  { type: "header", title: "MENU & PRODUCTS" },
  { title: "Menus", name: "menus", icon: "&#xe800;", status: true, allowedRoles: ["admin", "manager"], note: "Which menu each store shows, and when" },
  { title: "Products", name: "products", icon: "&#xf0f5;", status: true, allowedRoles: ["admin", "manager"], note: "Every item, its prices and its photo" },
  { title: "Featured products", name: "featured", icon: "&#xe80a;", status: true, allowedRoles: ["admin", "manager"], note: "The pizzas pinned to the top of the app's Most popular section" },
  { title: "Toppings & options", name: "option-items", icon: "&#xf217;", status: true, allowedRoles: ["admin", "manager"], note: "The choices customers add to an item, and what they cost" },

  // ── Operations ───────────────────────────────────
  { type: "header", title: "OPERATIONS" },
  { title: "Live orders", name: "live-orders", svgIcon: "ic-ops", status: true, allowedRoles: ["admin", "manager"], note: "What is in the kitchen right now, store by store, with recovery actions for stuck orders" },
  { title: "Group orders", name: "group-orders", svgIcon: "ic-users", status: true, allowedRoles: ["admin", "manager"], note: "Shared-cart sessions: who joined, what they added, and a way to close one" },
  {
    title: "Operations",
    name: "operations",
    svgIcon: "ic-ops",
    // PG bu grubu bilerek gizli tutuyor (bkz "hide operations menu" commiti).
    // Alti bastan asagi placeholder: Dispatch, Food Cost, P&L, Vision AI,
    // Voice AI. P&L'in ayri bir hikayesi var: eski anahtarlama mekanizmasi
    // adini "Pnl" diye tahmin ediyordu, bileselen adi ise "PnL" — yani
    // acilsa bile bos ekran veriyordu. Yeni yonlendirici tahmin etmiyor.
    status: false,
    groupOnly: true,
    subMenu: [
      { title: "Dispatch Board", name: "dispatch" },
      { title: "Food Cost", name: "food-cost" },
      { title: "P&L", name: "pnl" },
      { title: "Vision AI", name: "vision-ai" },
      { title: "Voice AI", name: "voice-ai" },
    ],
    subMenuType: "operations",
  },
  {
    // 2026-10-28 (owner): every stock page under one heading, named Inventory. The name stays "stocks": routes,
    // permissions and the store scope know the group by it.
    title: "Inventory",
    name: "stocks",
    svgIcon: "ic-stock",
    // mc_inv_items / mc_inv_movements PG sandbox'ta kurulu (2026-10-05,
    // ~/Downloads/connect_live_pg/pg_inv_install_nojson.sql). Canli PG'ye
    // yeni Inventory.php ile birlikte kurulana kadar canli API'de bu
    // sayfalar hata verir.
    status: true,
    // Basliga tiklamak eskiden dogrudan "stocks"a, yani asagidaki
    // "Inventory (old)" sayfasina gidiyordu; kullanici hicbir sey
    // secmeden eski sisteme dusuyordu. groupOnly bunu durdurur.
    groupOnly: true,
    subMenu: [
      // The assistant that asks the store manager for the month, one question at a time, and makes the Excel report.
      { title: "Monthly report", name: "stock-month-report" },
      { title: "Stock on hand", name: "stock-onhand" },
      { title: "All stores", name: "stock-overview" },
      { title: "Stock Count", name: "stock-count" },
      { title: "Deliveries", name: "stock-deliveries" },
      { title: "Waste", name: "stock-waste" },
      { title: "Reorder", name: "stock-reorder" },
      { title: "Stock list", name: "stock-list" },
      // Eski Invorest sayfalari: /inventory/api servisi PG'de yok, acilinca
      // 404 verir. Grup gizliyken de gorunmuyorlardi; oyle kaliyorlar.
      { title: "not used any more", divider: true, status: false },
      { title: "Inventory (old)", name: "stocks", status: false },
      { title: "Audits (old)", name: "stock-audits", status: false },
      { title: "Reducted Reports (old)", name: "stock-reducted-reports", status: false },
      { title: "Added Reports (old)", name: "stock-added-reports", status: false },
      // The old service's vendors and invoices, hidden as before, under the same heading.
      { title: "Vendors", name: "vendors", svgIcon: "ic-vendor", status: false },
      { title: "Invoices", name: "invoices", svgIcon: "ic-invoice", status: false },
    ],
    subMenuType: "stocks",
  },

  // ── Settings ─────────────────────────────────────
  { type: "header", title: "SETTINGS" },
  // Who can do what. Admin-only on purpose: the API refuses everyone else.
  { title: "Roles", name: "roles", svgIcon: "ic-settings", status: true, allowedRoles: ["admin"], note: "Build a set of permissions and give it a name" },
  { title: "Users", name: "panel-users", svgIcon: "ic-users", status: true, allowedRoles: ["admin"], note: "Who can sign in to this panel, and which role each one holds" },
  {
    title: "Branch Settings",
    name: "branch-settings",
    svgIcon: "ic-settings",
    status: true,
    allowedRoles: ["admin", "manager"],
    groupOnly: true,
    subMenu: [
      { title: "Working Hours", name: "working-hours", allowedRoles: ["admin", "manager"], note: "When each store opens for delivery and pickup" },
      { title: "Delivery Map", name: "delivery-map", allowedRoles: ["admin", "manager"], note: "The area a store delivers to, and the fee for each zone" },
      { title: "Till users", name: "pos-users", allowedRoles: ["admin", "manager"], note: "Who can sign in to the till in each store" },
    ],
    subMenuType: "branch-settings",
  },
  { title: "Loyalty Settings", name: "settings", svgIcon: "ic-loyalty", status: true, allowedRoles: ["admin", "manager"], note: "What each tier needs, the weekly streak rewards and the reward for inviting a friend" },
];

/**
 * The menu as one particular person should see it.
 *
 * @param {string|number} utype  mc_users.utype for the signed-in user
 * @param {string[]} hiddenPages route names hidden for this brand's build
 * @param {object|null} perms    the role's permissions from GET rbac/me
 * @param {{ allStores?: boolean }} [scope] allStores: the top bar shows "All stores"
 */
export function visibleMenu(utype, hiddenPages, perms, scope) {
  const hidden = hiddenPages || [];
  const allStores = !!(scope && scope.allStores);
  // perms: { "coupons.view": true, ... } from GET rbac/me, or null when unknown.
  // With a known set, a page whose permission is missing is dropped whatever
  // the utype says; the API refuses it anyway, so showing it would only mislead.
  const allowedByPermission = (item) => {
    if (!perms || !item.name) return true;
    const need = PAGE_PERMISSION[item.name];
    return !need || !!perms[need];
  };
  const userRole = utype == 1 ? "admin" : utype == 2 ? "manager" : "staff";

  const hasRoleAccess = (item) => {
    // Headings and dividers are not clickable, so they never carry a role.
    if (item.type === "header" || item.type === "subheader" || item.divider) return true;
    return (item.allowedRoles || ["admin"]).includes(userRole);
  };
  // Under "All stores" a page that needs one store leaves the menu. Only pages: a group stays while any of its
  // pages does (the Inventory group is itself named "stocks", like the old Invorest page).
  const isGroup = (item) => Array.isArray(item.items) || Array.isArray(item.subMenu);
  const notHidden = (item) =>
    !(item.name && (hidden.includes(item.name) || (allStores && !isGroup(item) && needsOneStore(item.name))));

  // status:false means "built but not ready, keep the code". It has to be
  // checked on nested items too: it used to be checked only at the top level,
  // so an unfinished page inside a group still showed.
  //
  // Who may see a page: once the API has said what this person's ROLE allows
  // (perms), the role decides for every page that has a permission — the old
  // utype rule (allowedRoles) only covers pages without one, and the case where
  // the API has no roles yet. Otherwise a store manager given notifications.send
  // would still lose "Push Notifications" to its admin-only allowedRoles.
  const keep = (item) => {
    if (item.status === false || !notHidden(item)) return false;
    const need = item.name ? PAGE_PERMISSION[item.name] : null;
    if (perms && need) return !!perms[need];
    // A group with children: with roles known, its children decide below.
    if (perms && (Array.isArray(item.items) || Array.isArray(item.subMenu))) return true;
    return hasRoleAccess(item) && allowedByPermission(item);
  };

  const items = [];
  let pendingHeader = null;
  const pushItem = (item) => {
    if (pendingHeader) {
      items.push(pendingHeader);
      pendingHeader = null;
    }
    items.push(item);
  };

  for (const item of mainMenu) {
    if (item.type === "header") {
      // Held back until something under it survives the filters, so a heading
      // with nothing beneath it never appears.
      pendingHeader = item.status === false ? null : item;
      continue;
    }

    if (!keep(item)) continue;

    const filteredItem = { ...item };
    if (Array.isArray(item.items)) {
      filteredItem.items = dropOrphanSubheaders(item.items.filter(keep));
      if (!filteredItem.items.some((sub) => sub.type !== "subheader")) continue;
    }
    if (Array.isArray(item.subMenu)) {
      filteredItem.subMenu = item.subMenu.filter(keep);
      if (!filteredItem.subMenu.some((sub) => !sub.divider)) continue;
    }

    pushItem(filteredItem);
  }
  return items;
}

/** A subheading whose pages were all filtered out is noise; drop it. */
function dropOrphanSubheaders(items) {
  return items.filter((item, i) => {
    if (item.type !== "subheader") return true;
    for (let j = i + 1; j < items.length; j++) {
      if (items[j].type === "subheader") return false;
      return true;
    }
    return false;
  });
}

/** Every clickable page in a filtered menu, flattened. */
export function visiblePages(menu) {
  const out = [];
  for (const item of menu) {
    if (item.type === "header") continue;
    const children = item.items || item.subMenu;
    if (children) {
      for (const c of children) if (c.name && !c.divider && c.type !== "subheader") out.push(c);
      continue;
    }
    if (item.name) out.push(item);
  }
  return out;
}

// "SALES REPORTS" -> "Sales Reports", keeping short all-caps words (CRM, SMS, HQ)
// but not the little joining words the headings are written with.
const SMALL_WORDS = new Set(["AND", "OR", "OF", "THE", "IN", "TO", "BY", "A", "AN"]);
const readable = (s) =>
  locale.value === "tr"
    ? readableTurkish(String(s || ""))
    : String(s || "")
        .split(" ")
        .map((w) => {
          if (SMALL_WORDS.has(w)) return w.toLowerCase();
          if (w.length <= 3 && w === w.toUpperCase()) return w;
          return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
        })
        .join(" ");

// In Turkish: the title's Turkish, and a heading written in capitals ("NE SATILDI, NE ZAMAN") as a sentence
// ("Ne satıldı, ne zaman"), cased the Turkish way (I → ı) and keeping its acronyms (CRM, SMS).
const ACRONYMS = new Set(["AI", "API", "BI", "CRM", "CSV", "GA4", "HQ", "KPI", "PDF", "POS", "ROI", "SKU", "SMS", "URL", "UTM"]);
function readableTurkish(s) {
  const text = tr(s);
  if (text !== text.toLocaleUpperCase("tr-TR")) return text;
  const lower = text
    .split(" ")
    .map((w) => (ACRONYMS.has(w.replace(/[^\p{L}\p{N}]/gu, "")) ? w : w.toLocaleLowerCase("tr-TR")))
    .join(" ");
  return lower.charAt(0).toLocaleUpperCase("tr-TR") + lower.slice(1);
}

/**
 * Every reachable page as a flat list with where it lives, for the sidebar
 * search. `path` reads like "Sales Reports › Money", so a result also tells
 * the reader where they would have found it by hand.
 */
// The icon a group shows in the sidebar when its own entry names none (the sidebar uses it too).
export const GROUP_ICON = { salesGroup: "ic-chart", dealEngineGroup: "ic-auto", crmAnalyticsGroup: "ic-crm" };

// Pages whose sidebar entry still uses a font glyph (`icon`): the line icon their page header shows.
const HEADER_ICON = {
  banners: "image",
  "push-notifications": "bell",
  sms: "ic-mail",
  coupons: "tag",
  menus: "ic-menu",
  products: "ic-products",
  featured: "ic-products",
  "option-items": "ic-modifier",
};

/**
 * What a page's header shows besides its own title: where the page lives in the
 * menu ("Sales Reports › Money"), the icon the sidebar shows next to it (or its
 * group's), and the menu's one-line note, for a page that has no subtitle of its
 * own. One lookup, so a page moved in the menu moves in its header too. `title` is
 * the page's name in the menu, for a screen that stands in for the page.
 */
export function pageMeta(name) {
  let section = "";
  for (const item of mainMenu) {
    if (item.type === "header") {
      section = item.title;
      continue;
    }
    const children = item.items || item.subMenu;
    if (children) {
      let sub = "";
      for (const c of children) {
        if (c.type === "subheader") {
          sub = c.title;
          continue;
        }
        if (c.name && c.name === name) {
          return {
            title: c.title,
            eyebrow: [section, item.title, sub].filter(Boolean).map(readable).join(" › "),
            icon: c.svgIcon || HEADER_ICON[name] || GROUP_ICON[item.type] || item.svgIcon || "ic-dashboard",
            note: c.note || "",
          };
        }
      }
      continue;
    }
    if (item.name && item.name === name) {
      return { title: item.title, eyebrow: readable(section), icon: item.svgIcon || HEADER_ICON[name] || "ic-dashboard", note: item.note || "" };
    }
  }
  return { title: "", eyebrow: "", icon: "ic-dashboard", note: "" };
}

export function searchIndex(menu) {
  const out = [];
  let section = "";
  for (const item of menu) {
    if (item.type === "header") {
      section = item.title;
      continue;
    }
    const children = item.items || item.subMenu;
    if (children) {
      let sub = "";
      for (const c of children) {
        if (c.type === "subheader") {
          sub = c.title;
          continue;
        }
        if (c.divider || !c.name || c.status === false) continue;
        out.push({ name: c.name, title: c.title, note: c.note || "", path: [section, item.title, sub].filter(Boolean).map(readable).join(" › ") });
      }
      continue;
    }
    if (item.name) out.push({ name: item.name, title: item.title, note: item.note || "", path: readable(section) });
  }
  return out;
}
